import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  InputNumber,
  Space,
  Tag,
  Typography,
} from "antd";

import {
  CheckOutlined,
  MinusOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const ProductCard = ({
  product,
  selected,
  inCart,
  cartItem,
  onSelect,
  onAdd,
  onQuantityChange,
}) => {
  /*
   * Local quantity is used while the user is preparing
   * the item before adding it to the purchase cart.
   *
   * Once the item is already in the cart, cartItem.quantity
   * becomes the source of truth.
   */
  const [quantity, setQuantity] = useState(
    cartItem?.quantity || 1
  );

  useEffect(() => {
    if (cartItem?.quantity) {
      setQuantity(cartItem.quantity);
    }
  }, [cartItem?.quantity]);

  const handleQuantityChange = (value) => {
    const nextQuantity = Math.max(
      1,
      Number(value || 1)
    );

    setQuantity(nextQuantity);

    /*
     * If the item is already in the cart,
     * update it immediately.
     */
    if (inCart && onQuantityChange) {
      onQuantityChange(
        product.id,
        nextQuantity
      );
    }
  };

  const increaseQuantity = () => {
    handleQuantityChange(quantity + 1);
  };

  const decreaseQuantity = () => {
    handleQuantityChange(
      Math.max(1, quantity - 1)
    );
  };

  const handleAdd = () => {
    onAdd(product, quantity);
  };

  const unitCost = Number(
    product.unitCost ??
      product.purchasePrice ??
      product.purchase_price ??
      product.cost ??
      0
  );

  const lineTotal =
    quantity * unitCost;

  return (
    <Card
      size="small"
      hoverable
      style={{
        borderRadius: 12,
        marginBottom: 10,

        border: selected
          ? "1px solid #1677ff"
          : inCart
          ? "1px solid #b7eb8f"
          : "1px solid #e5e7eb",

        background: selected
          ? "#f0f7ff"
          : inCart
          ? "#f6ffed"
          : "#fff",
      }}
      styles={{
        body: {
          padding: 14,
        },
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        {/* =====================================================
            SELECT
        ===================================================== */}

        <Checkbox
          checked={selected}
          onChange={() =>
            onSelect(product)
          }
        />

        {/* =====================================================
            PRODUCT INFORMATION
        ===================================================== */}

        <div
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Text
              strong
              ellipsis
              style={{
                maxWidth: 350,
              }}
            >
              {product.partName ||
                "Unnamed Part"}
            </Text>

            {inCart && (
              <Tag
                color="success"
                icon={
                  <CheckOutlined />
                }
              >
                In Cart
              </Tag>
            )}
          </div>

          <Space
            size={6}
            wrap
            style={{
              marginTop: 4,
            }}
          >
            <Text
              type="secondary"
              style={{
                fontSize: 12,
              }}
            >
              {product.partNumber ||
                "No Part Number"}
            </Text>

            {product.importerId && (
              <>
                <Text type="secondary">
                  •
                </Text>

                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                  }}
                >
                  {product.importerId}
                </Text>
              </>
            )}
          </Space>

          {/* UNIT PRICE */}
          <div
            style={{
              marginTop: 5,
            }}
          >
         <Text
  type="secondary"
  style={{
    fontSize: 12,
  }}
>
  Available Quantity:{" "}
</Text>

<Text
  strong
  style={{
    fontSize: 13,
  }}
>
  {product.availableQuantity}
</Text>
          </div>
        </div>

        {/* =====================================================
            QUANTITY
        ===================================================== */}

        <div
          style={{
            width: 155,
            flexShrink: 0,
          }}
        >
          <Text
            type="secondary"
            style={{
              display: "block",
              fontSize: 11,
              marginBottom: 4,
            }}
          >
            Order Quantity
          </Text>

          <Space.Compact
            style={{
              width: "100%",
            }}
          >
            <Button
              icon={<MinusOutlined />}
              onClick={
                decreaseQuantity
              }
            />

            <InputNumber
              min={1}
              value={quantity}
              controls={false}
              onChange={
                handleQuantityChange
              }
              style={{
                width: "100%",
                textAlign: "center",
              }}
            />

            <Button
              icon={<PlusOutlined />}
              onClick={
                increaseQuantity
              }
            />
          </Space.Compact>
        </div>

        {/* =====================================================
            TOTAL + ADD
        ===================================================== */}

        <div
          style={{
            width: 150,
            flexShrink: 0,
            textAlign: "right",
          }}
        >
          <Text
            type="secondary"
            style={{
              display: "block",
              fontSize: 11,
            }}
          >
            Order Total
          </Text>

          <Text
            strong
            style={{
              fontSize: 16,
              color: "#1677ff",
              display: "block",
              marginBottom: 6,
            }}
          >
            {lineTotal.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}
          </Text>

          <Button
            type={
              inCart
                ? "default"
                : "primary"
            }
            icon={
              inCart ? (
                <ShoppingCartOutlined />
              ) : (
                <PlusOutlined />
              )
            }
            onClick={handleAdd}
          >
            {inCart
              ? "Add More"
              : "Add to Cart"}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ProductCard;
